import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/20/solid';
import { CursorArrowRaysIcon, UsersIcon, CpuChipIcon, ClockIcon, ChartBarIcon, ServerStackIcon, BoltIcon, DocumentTextIcon, Battery0Icon } from '@heroicons/react/24/outline';
import { useState, useEffect, useRef } from 'react';
import Navbar from './Navbar';
import Queues from './Queues';
import { format } from 'date-fns';
import {
  VictoryChart,
  VictoryAxis,
  VictoryArea,
  VictoryTooltip,
  VictoryVoronoiContainer,
  VictoryPie,
} from "victory";

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function bytesToGB(bytes) {
  return (bytes / (1024 ** 3)).toFixed(2);
}

function secondsToHMS(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h}h ${m}m ${s}s`;
}

function ServerCpuChart({ data }) {
  const chartData = data.map((stat, idx) => ({
    x: stat.timestamp ? format(new Date(stat.timestamp), 'HH:mm:ss') : idx,
    y: stat.cpu,
  }));

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-8 w-full md:w-1/2">
      <h2 className="text-lg font-semibold mb-2">Server CPU Usage (%)</h2>
      <VictoryChart
        height={220}
        padding={{ top: 20, bottom: 40, left: 60, right: 20 }}
        containerComponent={
          <VictoryVoronoiContainer
            labels={({ datum }) => `${datum.x}\n${datum.y}%`}
            labelComponent={<VictoryTooltip cornerRadius={4} flyoutStyle={{ fill: "white" }} />}
          />
        }
      >
        <VictoryAxis
          label="Time"
          style={{
            axisLabel: { padding: 30, fontSize: 12 },
            tickLabels: { fontSize: 10, angle: -30, padding: 15 }
          }}
        />
        <VictoryAxis
          dependentAxis
          label="CPU (%)"
          domain={[0, 100]}
          style={{
            axisLabel: { padding: 40, fontSize: 12 },
            tickLabels: { fontSize: 10 }
          }}
        />
        <VictoryArea
          data={chartData}
          interpolation="monotoneX"
          style={{
            data: { fill: "rgba(239,68,68,0.2)", stroke: "#ef4444", strokeWidth: 2 }
          }}
        />
      </VictoryChart>
    </div>
  );
}

function RamUsagePie({ percent }) {
  const used = percent || 0;
  const free = 100 - used;
  return (
    <div className="bg-white rounded-lg shadow p-4 mb-8 flex flex-col items-center w-full md:w-1/2">
      <h2 className="text-lg font-semibold mb-2">RAM Usage (%)</h2>
      <VictoryPie
        height={110}
        width={220}
        data={[
          { x: "Used", y: used },
          { x: "Free", y: free }
        ]}
        colorScale={["#ef4444", "#d1d5db"]}
        innerRadius={35}
        labels={({ datum }) => `${datum.x}\n${datum.y.toFixed(1)}%`}
        labelRadius={45}
        style={{
          labels: { fontSize: 6, fill: "#374151", fontWeight: "bold" }
        }}
      />
    </div>
  );
}

function MariaDbConnectionsChart({ data }) {
  const chartData = data
    .filter(stat => stat.mariadbConnections !== undefined)
    .map((stat, idx) => ({
      x: stat.timestamp ? format(new Date(stat.timestamp), 'HH:mm:ss') : idx,
      y: stat.mariadbConnections,
    }));

  // If we don't have connection data, don't render the chart
  if (chartData.length === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-8 w-full md:w-1/2">
      <h2 className="text-lg font-semibold mb-2">MariaDB Connections</h2>
      <VictoryChart
        height={220}
        padding={{ top: 20, bottom: 40, left: 60, right: 20 }}
        containerComponent={
          <VictoryVoronoiContainer
            labels={({ datum }) => `${datum.x}\n${datum.y} connections`}
            labelComponent={<VictoryTooltip cornerRadius={4} flyoutStyle={{ fill: "white" }} />}
          />
        }
      >
        <VictoryAxis
          label="Time"
          style={{
            axisLabel: { padding: 30, fontSize: 12 },
            tickLabels: { fontSize: 10, angle: -30, padding: 15 }
          }}
        />
        <VictoryAxis
          dependentAxis
          label="Connections"
          style={{
            axisLabel: { padding: 40, fontSize: 12 },
            tickLabels: { fontSize: 10 }
          }}
        />
        <VictoryArea
          data={chartData}
          interpolation="monotoneX"
          style={{
            data: { fill: "rgba(59, 130, 246, 0.2)", stroke: "#3b82f6", strokeWidth: 2 }
          }}
        />
      </VictoryChart>
    </div>
  );
}

function LogViewer({ logs }) {
  const [expanded, setExpanded] = useState(false);
  
  if (!logs || logs.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-4 mb-8">
        <h2 className="text-lg font-semibold mb-2">PM2 Logs</h2>
        <p className="text-gray-500">No logs available</p>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-lg shadow p-4 mb-8">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold">PM2 Logs</h2>
        <button 
          onClick={() => setExpanded(!expanded)} 
          className="px-3 py-1 text-sm bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200"
        >
          {expanded ? 'Show Less' : 'Show More'}
        </button>
      </div>
      
      <div className="bg-gray-900 text-gray-200 p-4 rounded overflow-auto" style={{ maxHeight: expanded ? '500px' : '200px' }}>
        {logs.slice(0, expanded ? logs.length : 5).map((log, idx) => (
          <div key={idx} className="mb-2 font-mono text-xs border-b border-gray-700 pb-1">
            <div className="text-green-400">
              {log.timestamp ? new Date(log.timestamp).toLocaleString() : 'No timestamp'}
            </div>
            <div>{log.message || JSON.stringify(log)}</div>
          </div>
        ))}
        {!expanded && logs.length > 5 && (
          <div className="text-xs text-gray-400 mt-2">
            + {logs.length - 5} more logs
          </div>
        )}
      </div>
    </div>
  );
}

function ConnectionsList({ connections }) {
  const [expanded, setExpanded] = useState(false);
  
  if (!connections || connections.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-4 mb-8">
        <h2 className="text-lg font-semibold mb-2">Active Database Connections</h2>
        <p className="text-gray-500">No active connections</p>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-lg shadow p-4 mb-8">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold">Active Database Connections ({connections.length})</h2>
        <button 
          onClick={() => setExpanded(!expanded)} 
          className="px-3 py-1 text-sm bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200"
        >
          {expanded ? 'Show Less' : 'Show More'}
        </button>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Host</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DB</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">State</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {connections.slice(0, expanded ? connections.length : 5).map((conn) => (
              <tr key={conn.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{conn.id}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{conn.user}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{conn.host}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{conn.db || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{conn.time}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{conn.state || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!expanded && connections.length > 5 && (
          <div className="text-xs text-gray-500 mt-2 text-center">
            + {connections.length - 5} more connections
          </div>
        )}
      </div>
    </div>
  );
}

export default function ServerStats() {
  const [latestServerStat, setLatestServerStat] = useState(null);
  const [serverStats, setServerStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const serverStatsRef = useRef([]);
  const [backup, setBackup] = useState([]);

  console.log(backup);


  useEffect(() => {
    const fetchServerStats = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://api.insightos.co.uk/admin/stats', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();
        const statObj = data.data;
        if (statObj) {
          // Calculate RAM usage percentage if not provided by API
          const ramTotal = statObj.memoryTotal || 0;
          const ramUsed = statObj.memory || 0;
          const ramUsagePercent = ramTotal > 0 ? (ramUsed / ramTotal) * 100 : 0;
          
          const newStat = { 
            ...statObj, 
            timestamp: Date.now(),
            ramUsagePercent: ramUsagePercent
          };


          
          serverStatsRef.current = [
            ...serverStatsRef.current.slice(-29),
            newStat,
          ];
          setServerStats([...serverStatsRef.current]);
          setLatestServerStat(newStat);
          setBackup(statObj.backup || {});
        }
      } catch (error) {
        console.error('Error fetching server stats:', error);
        setError('Failed to load server statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchServerStats();
    const interval = setInterval(fetchServerStats, 60000);
    return () => clearInterval(interval);
  }, []);

  const serverStatCards = latestServerStat
    ? [
        {
          id: 1,
          name: 'CPU (%)',
          stat: latestServerStat.cpu?.toFixed(2),
          icon: CpuChipIcon,
        },
        {
          id: 2,
          name: 'Memory Used (GB)',
          stat: bytesToGB(latestServerStat.memory),
          icon: BoltIcon,
        },
        {
          id: 3,
          name: 'Uptime',
          stat: secondsToHMS(Math.floor((latestServerStat.uptime || 0) / 1000)),
          icon: ClockIcon,
        },
        {
          id: 4,
          name: 'Load Avg',
          stat: Array.isArray(latestServerStat.loadavg)
            ? latestServerStat.loadavg.map(x => x.toFixed(2)).join(', ')
            : '',
          icon: ChartBarIcon,
        },
        {
          id: 5,
          name: 'DB Connections',
          stat: latestServerStat.mariadbConnections !== undefined ? 
                latestServerStat.mariadbConnections : 'N/A',
          icon: Battery0Icon,
        },
        {
          id: 6,
          name: 'Active Queries',
          stat: latestServerStat.activeConnections !== undefined ? 
                latestServerStat.activeConnections : 'N/A',
          icon: ServerStackIcon,
        },
      ]
    : [];

  return (
    <>
      <Navbar />
      {loading && !latestServerStat && (
        <div className="flex justify-center items-center min-h-[300px]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
        </div>
      )}
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mx-4 mt-5" role="alert">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      )}
      
      {latestServerStat && (
        <>
          {/* Navigation Tabs */}
          <div className="px-4 sm:px-6 lg:px-8 mt-5">
            {backup && backup.last_backup && (
              <div className="mb-4 p-4 bg-emerald-100 border border-emerald-400 text-emerald-700 rounded-lg">
                <h3 className="font-semibold mb-2">Latest Backup</h3>
                <p><strong>Date:</strong> {backup.last_backup ? new Date(backup.last_backup).toLocaleString() : 'N/A'}</p>
              </div>
            )}
            <nav className="flex space-x-4 border-b border-gray-200">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-3 py-2 text-sm font-medium ${
                  activeTab === 'overview'
                    ? 'border-b-2 border-red-500 text-red-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('database')}
                className={`px-3 py-2 text-sm font-medium ${
                  activeTab === 'database'
                    ? 'border-b-2 border-red-500 text-red-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Database
              </button>
              <button
                onClick={() => setActiveTab('logs')}
                className={`px-3 py-2 text-sm font-medium ${
                  activeTab === 'logs'
                    ? 'border-b-2 border-red-500 text-red-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Logs
              </button>
              <button
                onClick={() => setActiveTab('queues')}
                className={`px-3 py-2 text-sm font-medium ${
                  activeTab === 'queues'
                    ? 'border-b-2 border-red-500 text-red-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Queues
              </button>
            </nav>
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <>
              <div>
                <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {serverStatCards.map((item) => (
                    <div
                      key={item.id}
                      className="relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow sm:px-6 sm:pt-6"
                    >
                      <dt>
                        <div className="absolute rounded-md bg-red-500 p-3">
                          <item.icon aria-hidden="true" className="size-6 text-white" />
                        </div>
                        <p className="ml-16 truncate text-sm font-medium text-gray-500">{item.name}</p>
                      </dt>
                      <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
                        <p className="text-2xl font-semibold text-red-500">{item.stat}</p>
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
              <div className="px-4 sm:px-6 lg:px-8 mt-10 flex flex-col md:flex-row gap-6">
                {serverStats.length > 0 && <ServerCpuChart data={serverStats} />}
                {latestServerStat && (
                  <RamUsagePie percent={latestServerStat.ramUsagePercent} />
                )}
              </div>
            </>
          )}

          {/* Database Tab */}
          {activeTab === 'database' && (
            <div className="px-4 sm:px-6 lg:px-8 mt-5">
              <div className="flex flex-col md:flex-row gap-6">
                {serverStats.length > 0 && <MariaDbConnectionsChart data={serverStats} />}
                
                <div className="w-full md:w-1/2 bg-white rounded-lg shadow p-4 mb-8">
                  <h2 className="text-lg font-semibold mb-2">Database Status</h2>
                  <table className="min-w-full divide-y divide-gray-200">
                    <tbody className="divide-y divide-gray-200">
                      {latestServerStat.mariadbConnectionStats && 
                        Object.entries(latestServerStat.mariadbConnectionStats).map(([key, value]) => (
                          <tr key={key}>
                            <td className="px-6 py-2 text-sm font-medium text-gray-900">{key}</td>
                            <td className="px-6 py-2 text-sm text-gray-500">{value}</td>
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                </div>
              </div>
              
              {latestServerStat.processlist && (
                <ConnectionsList connections={latestServerStat.processlist} />
              )}
            </div>
          )}

          {/* Logs Tab */}
          {activeTab === 'logs' && (
            <div className="px-4 sm:px-6 lg:px-8 mt-5">
              {latestServerStat.pm2Logs && (
                <LogViewer logs={latestServerStat.pm2Logs} />
              )}
            </div>
          )}

          {/* Queues Tab */}
          {activeTab === 'queues' && (
            <div className="px-4 sm:px-6 lg:px-8 mt-5">
              <Queues />
            </div>
          )}
        </>
      )}
    </>
  );
}
